/**
 * NotificationBell — sininho com contador e dropdown de notificações.
 *
 * Comportamento:
 *  - Exibe ícone de sino com badge contendo `unreadCount` (oculto se 0).
 *  - Click abre Popover com até 20 notificações in-memory (do
 *    `useNotificationsStore`).
 *  - Cada item mostra: nome do test_case/suite, badge colorido por status e
 *    horário relativo ("há 3 min") via `formatRelativeTime`.
 *  - Click em item navega para `/executions/:id`.
 *  - Botão "Marcar todas como lidas" chama `markAllRead()`.
 *  - Empty state quando não há notificações.
 *
 * Visibilidade:
 *  - Oculto quando `auth.mode === 'basic'` (admin global do sistema não recebe
 *    notificações de execuções — não tem tenant).
 *
 * Persistência: apenas in-memory. A rehidratação inicial é feita pelo
 * componente que monta o `useNotificationStream` (AppShell — task futura).
 */
import { Bell, CheckCheck, Inbox } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn, formatRelativeTime } from '@/lib/utils'
import { useAuthStore } from '@/store/auth.store'
import {
  useNotificationsStore,
  type Notification,
} from '@/store/notifications.store'
import type { ExecutionStatus } from '@/types/enums'

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------

/**
 * Configuração visual por status. Mantemos um helper local
 * porque o componente `StatusBadge` reutilizável é definido em outra task;
 * quando ele existir, é trivial substituir este helper por `<StatusBadge />`.
 */
const STATUS_CONFIG: Record<
  ExecutionStatus,
  { label: string; className: string }
> = {
  queued: { label: 'Na fila', className: 'bg-slate-100 text-slate-600' },
  running: { label: 'Executando', className: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Aprovado', className: 'bg-green-100 text-green-700' },
  rejected: { label: 'Reprovado', className: 'bg-red-100 text-red-700' },
  undetermined: {
    label: 'Não Determinado',
    className: 'bg-amber-100 text-amber-700',
  },
  cancelled: { label: 'Cancelado', className: 'bg-slate-100 text-slate-500' },
  error: { label: 'Erro', className: 'bg-orange-100 text-orange-700' },
}

function StatusPill({ status }: { status: ExecutionStatus }): React.ReactElement {
  const cfg = STATUS_CONFIG[status]
  return (
    <span
      data-testid={`notification-status-${status}`}
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        cfg.className,
      )}
    >
      {cfg.label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

export interface NotificationBellProps {
  className?: string
}

export function NotificationBell({
  className,
}: NotificationBellProps): React.ReactElement | null {
  const authMode = useAuthStore((s) => s.mode)
  const notifications = useNotificationsStore((s) => s.notifications)
  const unreadCount = useNotificationsStore((s) => s.unreadCount)
  const markAllRead = useNotificationsStore((s) => s.markAllRead)

  const navigate = useNavigate()

  // Admin global (HTTP Basic) não usa o sininho — não tem tenant nem execuções.
  if (authMode === 'basic') return null

  const hasUnread = unreadCount > 0
  const hasItems = notifications.length > 0
  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount)

  function handleItemClick(n: Notification): void {
    navigate(`/executions/${n.execution_id}`)
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          data-testid="notification-bell-trigger"
          aria-label={
            hasUnread
              ? `Notificações (${unreadCount} não lidas)`
              : 'Notificações'
          }
          className={cn('relative', className)}
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          {hasUnread ? (
            <span
              data-testid="notification-bell-badge"
              className="absolute -right-1 -top-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground"
            >
              {badgeText}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[360px] p-0"
        data-testid="notification-bell-panel"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="flex flex-col">
            <span className="text-sm font-semibold">Notificações</span>
            {hasUnread ? (
              <span className="text-xs text-muted-foreground">
                {unreadCount} não{' '}
                {unreadCount === 1 ? 'lida' : 'lidas'}
              </span>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!hasUnread}
            onClick={() => markAllRead()}
            data-testid="notification-bell-mark-all-read"
            className="h-8 gap-1 text-xs"
          >
            <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Marcar todas como lidas
          </Button>
        </div>

        {/* Lista */}
        {hasItems ? (
          <ul
            role="list"
            data-testid="notification-bell-list"
            className="max-h-[420px] divide-y divide-border overflow-y-auto"
          >
            {notifications.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => handleItemClick(n)}
                  className={cn(
                    'flex w-full flex-col gap-1 px-4 py-3 text-left transition-colors hover:bg-accent focus:bg-accent focus:outline-none',
                    !n.read && 'bg-accent/30',
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span
                      data-testid={`notification-item-title-${n.id}`}
                      className={cn(
                        'truncate text-sm',
                        !n.read ? 'font-semibold' : 'font-medium',
                      )}
                    >
                      {n.title}
                    </span>
                    <StatusPill status={n.status} />
                  </div>
                  <span
                    data-testid={`notification-item-time-${n.id}`}
                    className="text-xs text-muted-foreground"
                  >
                    {formatRelativeTime(n.timestamp)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div
            data-testid="notification-bell-empty"
            className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center"
          >
            <Inbox className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-muted-foreground">
              Nenhuma notificação por enquanto.
            </p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
